import { router, useLocalSearchParams } from "expo-router";

import { BackButton } from "@/blocks/BackButton";
import { ProfileInformationForm } from "@/blocks/ProfileInformationForm";
import { ScreenLayout } from "@/blocks/ScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnScrollView } from "@/components/VnScrollView";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useGetHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import { HouseholdMemberFormType } from "@/domain/householdMember";
import { useProfileInformationForm } from "@/hooks/useProfileInformationForm";
import { cleanHouseholdMemberFormData } from "@/utils/householdMember";

const HEADER_HEIGHT = 64;
const FOOTER_HEIGHT = 80;

export const ProfileInformationScreen = () => {
  const { householdMemberId } = useLocalSearchParams<{
    householdMemberId: string;
  }>();
  const { data: householdMember, isLoading } =
    useGetHouseholdMemberQuery(householdMemberId);
  const { mutateAsync: updateHouseholdMember } =
    useUpdateHouseholdMemberMutation();

  const { form, formState } = useProfileInformationForm({
    isPrimaryMember: Boolean(householdMember?.patientId),
  });

  const handleSubmit = async (formData: HouseholdMemberFormType) => {
    const cleanedData = cleanHouseholdMemberFormData(formData);

    await updateHouseholdMember(
      {
        id: householdMemberId,
        householdMemberData: cleanedData,
      },
      {
        onSuccess: _data => {
          router.back();
        },
      },
    );
  };

  return (
    <VnBox className="flex-1">
      <VnBox className="border-border bg-secondary absolute inset-x-0 top-0 z-50 h-16 flex-row items-center border-b">
        <BackButton className="mx-4 h-8" />
      </VnBox>
      <VnScrollView
        contentContainerStyle={{
          paddingTop: HEADER_HEIGHT,
          paddingBottom: FOOTER_HEIGHT,
        }}
        className="bg-background">
        <VnBox className="pb-8 pt-5">
          <ScreenLayout variant="FORM">
            <VnForm
              form={form}
              onSubmit={data => {
                void handleSubmit(data);
              }}>
              <VnCard className="rounded-md">
                <ProfileInformationForm
                  householdMember={householdMember}
                  isLoading={isLoading}
                />
              </VnCard>
            </VnForm>
          </ScreenLayout>
        </VnBox>
      </VnScrollView>
      <VnBox className="border-border bg-secondary absolute inset-x-0 bottom-0 z-50 h-20 border-t px-4 pt-4">
        <VnButton
          className="container mx-auto w-full"
          i18nKey="common:saveChanges"
          disabled={!formState.isDirty}
          onPress={() => {
            void form.handleSubmit(handleSubmit)();
          }}
        />
      </VnBox>
    </VnBox>
  );
};
